import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { RefreshCw, Search, Copy, ChevronLeft, ChevronRight } from "lucide-react";

const PROVIDERS = ["", "internal", "stripe", "ghl", "make", "twilio", "resend"];
const MODES = ["", "dry_run", "live"];
const STATUSES = ["", "success", "pending", "failed", "skipped"];
const PAGE_SIZE = 50;

const STATUS_COLOR = {
    success: { bg: "rgba(52, 211, 153, 0.15)", color: "var(--color-success)" },
    pending: { bg: "rgba(202, 138, 4, 0.15)", color: "var(--color-warning)" },
    failed: { bg: "rgba(248, 113, 113, 0.15)", color: "var(--color-danger)" },
    skipped: { bg: "rgba(110, 128, 153, 0.15)", color: "var(--color-text-dim)" },
};

const MODE_BADGE = {
    dry_run: { bg: "rgba(202, 138, 4, 0.18)", color: "var(--color-warning)", label: "DRY-RUN" },
    live: { bg: "rgba(52, 211, 153, 0.18)", color: "var(--color-success)", label: "LIVE" },
};

export default function AutomationEvents() {
    const [events, setEvents] = useState([]);
    const [count, setCount] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    // Filters
    const [provider, setProvider] = useState("");
    const [mode, setMode] = useState("");
    const [eventStatus, setEventStatus] = useState("");
    const [eventTypeQ, setEventTypeQ] = useState("");
    const [leadId, setLeadId] = useState("");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(0);

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({
            limit: String(PAGE_SIZE),
            skip: String(page * PAGE_SIZE),
        });
        if (provider) params.set("provider", provider);
        if (mode) params.set("mode", mode);
        if (eventStatus) params.set("status", eventStatus);
        if (eventTypeQ) params.set("event_type", eventTypeQ);
        if (leadId) params.set("lead_id", leadId);
        if (q) params.set("q", q);
        api.get(`/admin/automation-events?${params.toString()}`)
            .then((r) => {
                setEvents(r.data.events || []);
                setCount(r.data.count || 0);
                setTotal(r.data.total || 0);
            })
            .finally(() => setLoading(false));
    }, [provider, mode, eventStatus, eventTypeQ, leadId, q, page]);

    useEffect(() => { load(); }, [load]);

    const resetFilters = () => {
        setProvider(""); setMode(""); setEventStatus("");
        setEventTypeQ(""); setLeadId(""); setQ(""); setPage(0);
    };

    const copyPayload = async (e) => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(e.payload, null, 2));
            setCopiedId(e.id);
            setTimeout(() => setCopiedId(null), 1200);
        } catch { /* noop */ }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-6" data-testid="automation-events-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="a1-eyebrow mb-2">Automation</p>
                    <h1 className="text-3xl mb-1">Automation event log</h1>
                    <p style={{ color: "var(--color-text-muted)" }} className="text-sm">
                        Every Stripe / GoHighLevel / Make.com / internal automation action logs here, including dry-run actions.
                    </p>
                </div>
                <Link to="/admin/shadow-inbox" className="a1-btn-ghost text-sm" data-testid="automation-events-link-shadow-inbox">
                    Shadow Inbox →
                </Link>
            </div>

            {/* Filters */}
            <div className="a1-card a1-card-feature">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                        <label className="a1-label">Provider</label>
                        <select className="a1-select" value={provider} onChange={(e) => { setProvider(e.target.value); setPage(0); }} data-testid="filter-provider">
                            {PROVIDERS.map((p) => <option key={p} value={p}>{p || "All providers"}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="a1-label">Mode</label>
                        <select className="a1-select" value={mode} onChange={(e) => { setMode(e.target.value); setPage(0); }} data-testid="filter-mode">
                            {MODES.map((m) => <option key={m} value={m}>{m ? m.toUpperCase().replace("_", "-") : "All modes"}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="a1-label">Status</label>
                        <select className="a1-select" value={eventStatus} onChange={(e) => { setEventStatus(e.target.value); setPage(0); }} data-testid="filter-status">
                            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="a1-label">Event type contains</label>
                        <input
                            className="a1-input"
                            value={eventTypeQ}
                            onChange={(e) => { setEventTypeQ(e.target.value); setPage(0); }}
                            placeholder="e.g. invoice"
                            data-testid="filter-event-type"
                        />
                    </div>
                    <div>
                        <label className="a1-label">Lead ID</label>
                        <input
                            className="a1-input"
                            value={leadId}
                            onChange={(e) => { setLeadId(e.target.value); setPage(0); }}
                            placeholder="exact match"
                            data-testid="filter-lead-id"
                        />
                    </div>
                    <div>
                        <label className="a1-label flex items-center gap-1.5"><Search size={12} /> Search customer / lead</label>
                        <input
                            className="a1-input"
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setPage(0); }}
                            placeholder="name, email, phone, lead id"
                            data-testid="filter-q"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <button onClick={load} className="a1-btn-secondary" data-testid="automation-refresh">
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button onClick={resetFilters} className="a1-btn-ghost" data-testid="filter-reset">
                        Reset filters
                    </button>
                    <span className="text-sm ml-auto" style={{ color: "var(--color-text-muted)" }} data-testid="automation-count">
                        {loading ? "Loading…" : `Page ${page + 1} of ${totalPages} · Showing ${count} of ${total} event${total === 1 ? "" : "s"}`}
                    </span>
                </div>
            </div>

            {/* Events list */}
            <div className="space-y-2">
                {events.map((e) => {
                    const colors = STATUS_COLOR[e.status] || STATUS_COLOR.pending;
                    const modeStyle = MODE_BADGE[e.mode] || MODE_BADGE.dry_run;
                    const open = openId === e.id;
                    return (
                        <div
                            key={e.id}
                            className="a1-card"
                            data-testid={`automation-event-${e.id}`}
                        >
                            <div
                                className="flex items-center justify-between gap-4 cursor-pointer"
                                onClick={() => setOpenId(open ? null : e.id)}
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-mono text-xs" style={{ color: "var(--color-text-dim)" }}>
                                        {e.timestamp.replace("T", " ").slice(0, 19)}
                                    </p>
                                    <p className="text-base font-semibold leading-tight truncate" style={{ color: "var(--color-text)" }}>
                                        {e.event_type}
                                    </p>
                                    <p className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
                                        {e.provider} · actor: {e.actor || "system"}
                                        {e.lead_id ? ` · lead: ${e.lead_id.slice(0, 8)}` : ""}
                                    </p>
                                </div>
                                <span
                                    className="a1-badge uppercase tracking-wider whitespace-nowrap"
                                    style={{ background: modeStyle.bg, color: modeStyle.color }}
                                    data-testid={`event-mode-${e.id}`}
                                >
                                    {modeStyle.label}
                                </span>
                                <span
                                    className="a1-badge uppercase tracking-wider whitespace-nowrap"
                                    style={{ background: colors.bg, color: colors.color }}
                                    data-testid={`event-status-${e.id}`}
                                >
                                    {e.status}
                                </span>
                            </div>
                            {open ? (
                                <div className="mt-3 space-y-2">
                                    <div className="flex justify-end">
                                        <button
                                            onClick={(ev) => { ev.stopPropagation(); copyPayload(e); }}
                                            className="a1-btn-ghost"
                                            data-testid={`copy-payload-${e.id}`}
                                        >
                                            <Copy size={12} /> {copiedId === e.id ? "Copied!" : "Copy payload"}
                                        </button>
                                    </div>
                                    <pre
                                        className="p-3 rounded-md text-xs overflow-x-auto"
                                        style={{
                                            background: "var(--color-bg-base)",
                                            color: "var(--color-text-muted)",
                                            border: "1px solid var(--color-line)",
                                        }}
                                    >
                                        {JSON.stringify(e.payload, null, 2)}
                                        {e.error ? `\n\nERROR: ${e.error}` : ""}
                                    </pre>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
                {!loading && events.length === 0 ? (
                    <p className="a1-card text-sm" style={{ color: "var(--color-text-muted)" }}>
                        No automation events match these filters. Try resetting filters or trigger a dry-run action from a lead detail page.
                    </p>
                ) : null}
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE ? (
                <div className="flex items-center justify-center gap-3" data-testid="automation-pagination">
                    <button
                        className="a1-btn-secondary"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                        data-testid="page-prev"
                    >
                        <ChevronLeft size={14} /> Prev
                    </button>
                    <span style={{ color: "var(--color-text-muted)" }} className="text-sm">
                        Page {page + 1} of {totalPages}
                    </span>
                    <button
                        className="a1-btn-secondary"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page + 1 >= totalPages}
                        data-testid="page-next"
                    >
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            ) : null}
        </div>
    );
}
